import {
  defineDoctype,
  normalizeLifecycleContract,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
  type DocumentLifecycleContract,
} from "@kubdee/sdk";
import { bookingVehicles } from "./schemas/booking-vehicles";
import { pickupVehicleLines } from "./schemas/pickup-vehicle-lines";
import { rentalBookings } from "./schemas/rental-bookings";
import { rentalLocations } from "./schemas/rental-locations";
import { rentalPenalties } from "./schemas/rental-penalties";
import { rentalPickups } from "./schemas/rental-pickups";
import { rentalReturns } from "./schemas/rental-returns";
import { rentalSettings } from "./schemas/rental-settings";
import { returnVehicleLines } from "./schemas/return-vehicle-lines";
import { vehicleImages } from "./schemas/vehicle-images";
import { vehicles } from "./schemas/vehicles";

// ============================================================================
// Car-rental module — registered doctypes.
//
// Plugged into the boot loop via `modules/car-rental/index.ts → registeredDoctypes`;
// `apps/tenant-worker/src/middleware/auto-migrate.ts` upserts these into
// `core_doctype_registry` on the first request per tenant. Any entry whose
// `defaultSeries` is set is also upserted into `core_numbering_series` with
// `(module, doctype, company_id=NULL)` so a fresh tenant can run the live
// numbering call sites without per-company seeding (#347 / #423).
//
// Doctype id rule (#426):
//   - Doctype identifier is ALWAYS singular — the same string is used by
//     the registry, numbering, workflow_workflows.doctype, permissions,
//     PermissionGate, hooks, and reference fields. For booking that is
//     `"booking"`. Tracking down "where is this string used?" is a grep
//     for `"booking"` rather than juggling singular/plural variants.
//   - URL path segments stay plural by REST convention — `/car-rental/bookings`
//     is the collection endpoint, not a doctype identifier. Same for
//     react-query cache keys that mirror the URL (`[MODULES.CAR_RENTAL,
//     "bookings"]`). Database table names also stay plural (collection).
//   - Pre-#426 the workflow + permissions subsystems used the plural
//     `"bookings"` key while the registry used singular — required a
//     bridge alias in WorkflowTab and produced silent "ยังไม่มี workflow"
//     empty states. The bridge has been removed; the data migrated. New
//     contributors: keep the rule. New modules MUST keep the doctype id
//     consistent across every surface from day one.
//
// Pickup vs Release id caveat — read before renaming:
//   - #420 renamed the table + column release → pickup
//     (`car_rental_rental_pickups.pickup_no`) and the UI labels
//     "ปล่อยรถ" → "รับรถ", BUT the numbering call site
//     (`services/release.service.ts:85`) and the legacy seed row
//     (`seed.ts:61`) still use the doctype literal `"release"`. To keep
//     the existing per-company counter resolvable AND the platform
//     default useful, the registry id stays `"release"` here even though
//     the table/column have been renamed. Migrating the doctype id from
//     `"release"` → `"pickup"` requires updating the call site + a data
//     migration on `core_numbering_series` and is tracked separately.
// ============================================================================

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

export const bookingLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "workflow_state",
    docStatusField: "doc_status",
    draftStates: ["booked"],
    submittedStates: [
      "confirmed",
      "deposit_received",
      "vehicle_picked_up",
      "vehicle_returned",
      "refund_completed",
      "closed",
    ],
    cancelledStates: ["cancelled"],
    immutableStates: ["closed", "cancelled"],
    editAllowedStates: [
      "booked",
      "confirmed",
      "deposit_received",
      "vehicle_picked_up",
    ],
    actions: {
      submit: {
        label: "ยืนยัน",
        from: ["booked"],
        to: "confirmed",
        docStatus: 1,
        reversalPolicy: "none",
        activityAction: "confirmed",
      },
      cancel: {
        label: "ยกเลิก",
        from: [
          "booked",
          "confirmed",
          "deposit_received",
          "vehicle_picked_up",
          "vehicle_returned",
          "refund_completed",
          "closed",
        ],
        to: "cancelled",
        docStatus: 2,
        reversalPolicy: "custom",
        requiresReason: true,
        activityAction: "cancelled",
      },
    },
  });

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

const SYSTEM_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

/**
 * Rental booking (`การจองรถเช่า`). Flagship transactional doctype for the
 * car-rental module; already drives the workflow engine (#16/#17/#18) and
 * has the richest custom-form surface on the platform.
 *
 * Numbering writes the generated value to `booking_no`. Prefix mirrors
 * the legacy `seed.ts` value (`{A}{A}{0}{0}{0}{0}` — uppercase letters +
 * numeric counter, no year reset) verbatim so existing per-company
 * counters keep their running value when the platform-default NULL row
 * lands.
 */
export const bookingDoctype = defineDoctype({
  id: "booking",
  moduleId: "car-rental",
  table: rentalBookings,
  label: "การจองรถเช่า",
  icon: "ClipboardList",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "booking_no",
  displayField: "booking_no",
  imageField: null,
  searchFields: ["booking_no", "workflow_state"],
  columnLabels: {
    booking_no: "เลขที่การจอง",
    customer_id: "ลูกค้า",
    vehicle_id: "รถเช่า",
    pickup_branch_id: "สาขาที่รับรถ",
    return_branch_id: "สาขาที่คืนรถ",
    pickup_datetime: "วันเวลารับรถ",
    return_datetime: "วันเวลาคืนรถ",
    actual_return_datetime: "คืนรถจริง",
    rental_days: "จำนวนวัน",
    daily_rate: "ค่าเช่าต่อวัน",
    rental_amount: "ยอดค่าเช่า",
    with_driver: "มีคนขับ",
    driver_rate_per_day: "ค่าคนขับต่อวัน",
    total_driver_cost: "ยอดค่าคนขับ",
    additional_charges: "ค่าใช้จ่ายเพิ่มเติม",
    discount_amount: "ส่วนลด",
    late_hours: "ชั่วโมงคืนล่าช้า",
    total_late_fee: "ยอดค่าปรับล่าช้า",
    tax_amount: "ภาษี",
    security_deposit: "เงินประกัน",
    refund_amount: "เงินคืน",
    doc_status: "สถานะเอกสาร",
    workflow_state: "สถานะ",
  },
  defaultSeries: {
    prefix: "{A}{A}{0}{0}{0}{0}",
    resetEvery: "never",
    seriesName: "Standard",
  },
  workflowTemplate: {
    name: "การจองรถเช่า",
    description: "จอง → ยืนยัน → มัดจำ → รับรถ → คืนรถ → คืนเงิน → ปิดงาน",
    isDefault: true,
    states: [
      { key: "booked", label: "จอง", stateType: "start", color: "#F97316", sortOrder: 0, positionX: 380, positionY: 50, docStatus: 0 },
      { key: "confirmed", label: "ยืนยัน", stateType: "intermediate", color: "#3B82F6", sortOrder: 1, positionX: 380, positionY: 170, docStatus: 1 },
      { key: "deposit_received", label: "รับมัดจำ", stateType: "intermediate", color: "#8B5CF6", sortOrder: 2, positionX: 380, positionY: 290, docStatus: 1 },
      { key: "vehicle_picked_up", label: "รับรถ", stateType: "intermediate", color: "#06B6D4", sortOrder: 3, positionX: 380, positionY: 410, docStatus: 1 },
      { key: "vehicle_returned", label: "คืนรถ", stateType: "intermediate", color: "#8B5CF6", sortOrder: 4, positionX: 380, positionY: 530, docStatus: 1 },
      { key: "refund_completed", label: "คืนเงินประกัน", stateType: "intermediate", color: "#06B6D4", sortOrder: 5, positionX: 380, positionY: 650, docStatus: 1 },
      { key: "closed", label: "ปิดงาน", stateType: "success", color: "#22C55E", sortOrder: 6, positionX: 380, positionY: 770, docStatus: 1 },
      { key: "cancelled", label: "ยกเลิก", stateType: "fail", color: "#EF4444", sortOrder: 7, positionX: 680, positionY: 170, docStatus: 2 },
    ],
    transitions: [
      { fromStateKey: "booked", toStateKey: "confirmed", label: "ยืนยัน", formType: "custom", customFormKey: "car-rental.confirm-booking", actionHook: "car-rental.action.confirmBooking" },
      { fromStateKey: "booked", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "confirmed", toStateKey: "deposit_received", label: "รับมัดจำ", formType: "custom", customFormKey: "car-rental.deposit-payment", actionHook: "car-rental.action.receiveDeposit" },
      { fromStateKey: "confirmed", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "deposit_received", toStateKey: "vehicle_picked_up", label: "รับรถ", formType: "custom", customFormKey: "car-rental.release-vehicle", actionHook: "car-rental.action.releaseVehicle" },
      { fromStateKey: "deposit_received", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "vehicle_picked_up", toStateKey: "vehicle_returned", label: "คืนรถ", formType: "custom", customFormKey: "car-rental.return-vehicle", actionHook: "car-rental.action.returnVehicle" },
      { fromStateKey: "vehicle_picked_up", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "vehicle_returned", toStateKey: "refund_completed", label: "คืนเงินประกัน", formType: "custom", customFormKey: "car-rental.refund-security", actionHook: "car-rental.action.refundSecurity" },
      { fromStateKey: "vehicle_returned", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "refund_completed", toStateKey: "closed", label: "ปิดงาน", actionHook: "car-rental.action.closeBooking" },
      { fromStateKey: "refund_completed", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
      { fromStateKey: "closed", toStateKey: "cancelled", label: "ยกเลิก", actionHook: "car-rental.action.cancelBooking" },
    ],
  },
  lifecycle: bookingLifecycle,
});

/**
 * Pickup (`รับรถ`) — vehicle handover document. Renamed from "release"
 * at the table/column level in #420, but the numbering call site
 * (`services/release.service.ts:85`) still uses doctype `"release"`, so
 * the registry id stays `"release"` until that migration lands. The
 * `numberingField` and `tableName` reflect the post-#420 names because
 * the column physically holds the value.
 */
export const releaseDoctype = defineDoctype({
  id: "release",
  moduleId: "car-rental",
  table: rentalPickups,
  label: "เอกสารรับรถ",
  icon: "FileOutput",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "pickup_no",
  displayField: "pickup_no",
  imageField: null,
  searchFields: ["pickup_no", "status"],
  columnLabels: {
    pickup_no: "เลขที่รับรถ",
    booking_id: "การจอง",
    actual_pickup_datetime: "วันเวลารับจริง",
    picked_up_by: "ผู้รับรถ",
    mileage_out: "เลขไมล์ออก",
    fuel_level_out: "ระดับน้ำมันออก",
    vehicle_condition: "สภาพรถ",
    existing_damage_notes: "หมายเหตุความเสียหายเดิม",
    pickup_remarks: "หมายเหตุการรับรถ",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "RL-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
});

/**
 * Return (`คืนรถ`) — vehicle return document. Numbering writes to
 * `return_no`; prefix `RT-{YYYY}-#####` mirrors the legacy `seed.ts`
 * value verbatim so existing per-company counters keep their running
 * value when the platform-default NULL row lands.
 */
export const returnDoctype = defineDoctype({
  id: "return",
  moduleId: "car-rental",
  table: rentalReturns,
  label: "เอกสารคืนรถ",
  icon: "RotateCcw",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "return_no",
  displayField: "return_no",
  imageField: null,
  searchFields: ["return_no", "status"],
  columnLabels: {
    return_no: "เลขที่คืนรถ",
    booking_id: "การจอง",
    pickup_id: "เอกสารรับรถ",
    return_datetime: "วันเวลาคืน",
    received_by: "ผู้รับคืน",
    returned_by: "ผู้ส่งคืน",
    odometer_reading: "เลขไมล์",
    fuel_level: "ระดับน้ำมัน",
    exterior_condition: "สภาพภายนอก",
    interior_condition: "สภาพภายใน",
    total_penalty: "ค่าปรับรวม",
    refund_amount: "เงินคืน",
    return_remarks: "หมายเหตุ",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "RT-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
});

export const rentalVehicleDoctype = defineDoctype({
  id: "rental_vehicle",
  moduleId: "car-rental",
  table: vehicles,
  label: "รถเช่า",
  icon: "Car",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "fleet_vehicle_id",
  imageField: null,
  searchFields: ["fleet_vehicle_id", "calculation_method"],
});

export const rentalVehicleImageDoctype = defineDoctype({
  id: "rental_vehicle_image",
  moduleId: "car-rental",
  table: vehicleImages,
  label: "รูปรถเช่า",
  icon: "Image",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("rental_vehicle", "images", "vehicle_id", "sort_order"),
  numberingField: null,
  displayField: "caption",
  imageField: "image_url",
  searchFields: ["caption"],
});

export const rentalLocationDoctype = defineDoctype({
  id: "rental_location",
  moduleId: "car-rental",
  table: rentalLocations,
  label: "สถานที่รับ/คืนรถ",
  icon: "MapPin",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "address", "contact_phone"],
});

export const bookingVehicleDoctype = defineDoctype({
  id: "booking_vehicle",
  moduleId: "car-rental",
  table: bookingVehicles,
  label: "รถในการจอง",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("booking", "vehicles", "booking_id", "sort_order"),
  numberingField: null,
  displayField: "vehicle_id",
  imageField: null,
  searchFields: ["booking_id", "vehicle_id", "status"],
});

export const pickupVehicleLineDoctype = defineDoctype({
  id: "pickup_vehicle_line",
  moduleId: "car-rental",
  table: pickupVehicleLines,
  label: "รายการรถในใบรับรถ",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("release", "vehicle_lines", "pickup_id", "sort_order"),
  numberingField: null,
  displayField: "vehicle_id",
  imageField: null,
  searchFields: ["pickup_id", "booking_id", "vehicle_id"],
});

export const returnVehicleLineDoctype = defineDoctype({
  id: "return_vehicle_line",
  moduleId: "car-rental",
  table: returnVehicleLines,
  label: "รายการรถในใบคืนรถ",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("return", "vehicle_lines", "return_id", "sort_order"),
  numberingField: null,
  displayField: "vehicle_id",
  imageField: null,
  searchFields: ["return_id", "booking_id", "vehicle_id"],
});

export const rentalPenaltyDoctype = defineDoctype({
  id: "rental_penalty",
  moduleId: "car-rental",
  table: rentalPenalties,
  label: "ค่าปรับการคืนรถ",
  icon: "ReceiptText",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("return", "penalties", "return_id"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["return_id", "description", "remarks"],
});

export const rentalSettingsDoctype = defineDoctype({
  id: "rental_settings",
  moduleId: "car-rental",
  table: rentalSettings,
  label: "ตั้งค่าเช่ารถ",
  icon: "Settings",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "single",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "company_id",
  imageField: null,
  searchFields: ["company_id", "workflow_type", "default_calculation_method"],
});

/**
 * Every doctype the car-rental module wants registered with the platform.
 * Order is preserved in the Admin UI through `listDoctypes()` sort.
 */
export const carRentalDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  rentalVehicleDoctype,
  rentalVehicleImageDoctype,
  rentalLocationDoctype,
  bookingDoctype,
  bookingVehicleDoctype,
  releaseDoctype,
  pickupVehicleLineDoctype,
  returnDoctype,
  returnVehicleLineDoctype,
  rentalPenaltyDoctype,
  rentalSettingsDoctype,
]);
